import styled from "styled-components"
import useTranslation from "../../hooks/useTranslation"
import Alert from "../toast/Alert"
import { FlexBox } from "../generic/Layout"
import { Button } from "../generic/Core"
import ModalForm from "../modal/ModalForm"
import { useState } from "react"
import resolveModalForm from "../../utils/model/resolveModalForm"
import RButton from "../generic/RButton"
import { useNavigate } from "react-router-dom"
import { conditionFields, evaluateCondition } from "../../../../common/model/condition/evaluate"
import { useQueryClient } from "@tanstack/react-query"
import { useStore } from "../pageView/Page"

const NotificationBarWrapper = styled.div`
    margin: 1rem 0 -1rem 0;
`

const checkConditions = (notifications, data) => {
    return notifications.filter(notification => {
        // A value missing from the record counts as false here. That is
        // long-standing behaviour of this bar, kept deliberately — the shared
        // evaluator compares strictly, so the coercion happens on the way in.
        const values = {}
        for (const f of conditionFields(notification.condition)) {
            values[f] = data?.[f] === undefined ? false : data[f]
        }
        return evaluateCondition(notification.condition, { values })
    })
}

const NotificationBar = ({ notifications }) => {
    // The fields these conditions read, so the bar subscribes to exactly those.
    //
    // This asked each condition for `c.key`, and no condition has ever had one —
    // the predicate language spells it `field`. So the subscription was always
    // `['entity', 'id', undefined]`, every subject read as undefined, and
    // `checkConditions` coerced undefined to false. A booked invoice therefore
    // matched `hasPayment equals false` forever and `paid equals true` never:
    // paying it changed nothing on screen. The paid and partially-paid banners
    // could not render at all, which is why nothing looked broken until someone
    // paid an invoice and waited for the banner to change.
    //
    // `conditionFields` is the one answer to this question and was already
    // imported in this file. It also walks `all`/`any` nesting and drops
    // `$`-prefixed subjects, neither of which the hand-rolled version did.
    const conditionKeys = notifications
        ? notifications.flatMap(n => conditionFields(n.condition))
        : []
    const neededKeys = ['entity', 'id', ...new Set(conditionKeys)]

    const [state, setState] = useStore(s =>
        Object.fromEntries(neededKeys.map(k => [k, s[k]]))
    )
    const [activeModal, setActiveModal] = useState(null)
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    if (!notifications) return null

    const validNotifications = checkConditions(notifications, state)
    const showNotifications = validNotifications.length > 0
    const { t } = useTranslation()

    if (!showNotifications) return null


    const handleClick = (button, index) => {
        const { actionType, path } = button

        if (actionType === 'showModal') {
            setActiveModal(index)
        }
        if (actionType === 'navigate') {
            navigate(path)
        }
    }
    
    // Callback when modal closes - invalidate queries to trigger refetch
    const handleModalClose = () => {
        setState({ loaded: false })
    }
    
    // Callback when StateForm updates - invalidate queries to refresh data
    const handleUpdate = async () => {
        // Invalidate the entity query to force a fresh fetch
        if (state?.entity && state?.id) {
            await queryClient.invalidateQueries({ queryKey: [state.entity, state.id] })
        }
        setState({ loaded: false })
    }


    return validNotifications.map(
            (notification, i) => {
                //The form the button opens
                const modalForm = resolveModalForm(notification?.button)
                // The bar has no view key of its own, so the record's entity and
                // the button's action name it — the same model-derived shape the
                // rest of the suite relies on.
                const testId = `${state?.entity}.notification.${notification.button?.action}`
                return (
                    <div key={i}>
                        <NotificationBarWrapper>
                            <Alert className={notification.type} >
                                <FlexBox $justify='space-between'>
                                    <FlexBox $alignItems='center'>
                                        <Alert.Icon type={notification.type} size={'1.5rem'} />
                                        {t(notification.message)}
                                    </FlexBox>
                                    {notification.button &&
                                        <RButton onClick={() => handleClick(notification.button, i)} $variant={notification.button.variant}
                                            label={notification.button.label} icon={notification.button.icon} $brightBackground={true}
                                            testId={testId}
                                        >{t(notification.button.label)}</RButton>
                                    }
                                </FlexBox>
                            </Alert>
                        </NotificationBarWrapper>
                        {activeModal === i && <ModalForm
                            name='ModalForm'
                            mode='fullscreen'
                            isVisible={activeModal === i}
                            setIsVisible={() => setActiveModal(null)}
                            modalTitle={notification.button?.label || notification.message}
                            id={modalForm.id}
                            groups={modalForm.elements}
                            buttons={modalForm.buttons}
                            submit={modalForm.submit}
                            useStore={useStore}
                            parentId={state?.id}
                            testId={testId}
                            onUpdate={handleUpdate}
                            onClose={handleModalClose}
                        />}
                    </div>
                )
            }
        )
}


export default NotificationBar
